import * as React from 'react';
import classNames from 'classnames';
import { Tooltip } from '@revfluence/fresh';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { CopyIcon } from '@revfluence/fresh-icons/regular/esm';

interface IProps {
  align?: 'left' | 'center' | 'right';
  className?: string;
  width?: string | number;
  tooltip?: string;
  clickToCopy?: boolean;
}

export const EllipsisLabel: React.FC<React.PropsWithChildren<IProps>> = ({
  align = 'left',
  className = '',
  width = 100,
  tooltip,
  clickToCopy = false,
  children,

}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const { toast } = useToast();

  // Check if the content is overflowing
  React.useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      setIsOverflowing(contentElement.scrollWidth > contentElement.clientWidth);
    }
  }, [children, width]);

  const handleCopy = React.useCallback(async () => {
    if (!clickToCopy) return;

    try {
      const textToCopy = typeof children === 'string' ? children : contentRef.current?.textContent || '';
      await navigator.clipboard.writeText(textToCopy);

      toast({
        title: 'Copied to clipboard',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error copying to clipboard',
        variant: 'destructive',
      });
    }
  }, [clickToCopy, children, toast]);

  const content = (
    <div
      className={classNames(
        'flex items-center gap-1',
        className,
      )}
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
    >
      <div
        ref={contentRef}
        className={classNames(
          'overflow-hidden whitespace-nowrap text-ellipsis',
          {
            'text-left': align === 'left',
            'text-center': align === 'center',
            'text-right': align === 'right',
          },
          'text-grey-6',
        )}
      >
        {children}
      </div>
      {clickToCopy && (
        <CopyIcon
          className="w-4 h-4 text-grey-4 flex-shrink-0 cursor-pointer hover:text-grey-6 transition-colors"
          onClick={handleCopy}
        />
      )}
    </div>
  );

  // If content is overflowing and tooltip is provided, show tooltip
  if (isOverflowing && tooltip) {
    return (
      <Tooltip title={tooltip}>
        {content}
      </Tooltip>
    );
  }

  return content;
};
